import { makeTitrationTrial } from "./models";
import type {
  AmmeterPlacement,
  TitrationTrial,
  VoltmeterPlacement,
} from "./models";

export type RecordedElectricalPoint = {
  lampVoltageV: number;
  currentA: number;
  resistanceOhm: number;
};

export type TitrationSessionState = {
  dispensedMl: number;
  tapOpenFraction: number;
  preferredTapOpenFraction?: number;
  airBubblePresent: boolean;
  eyeLevel: boolean;
  indicatorAdded: boolean;
  swirled: boolean;
  trials: TitrationTrial[];
  answer: string;
  analysisFeedback: string | null;
};

export type ElectricalSessionState = {
  supplyVoltageV: number;
  seriesResistanceOhm: number;
  switchClosed: boolean;
  ammeterPlacement: AmmeterPlacement;
  voltmeterPlacement: VoltmeterPlacement;
  ammeterRangeA: number;
  voltmeterRangeV: number;
  points: RecordedElectricalPoint[];
  reason: string;
  analysisFeedback: string | null;
};

export type ExperimentSessionsState = {
  titration: TitrationSessionState;
  electrical: ElectricalSessionState;
};

export function recordCurrentTitrationTrial(
  current: TitrationSessionState
): TitrationSessionState {
  if (!current.indicatorAdded || !current.swirled || current.dispensedMl < 1) {
    return current;
  }

  return {
    ...current,
    trials: [
      ...current.trials,
      makeTitrationTrial(
        current.dispensedMl,
        current.airBubblePresent,
        current.eyeLevel
      ),
    ],
    analysisFeedback: null,
  };
}

export function refillForNextTitrationTrial(
  current: TitrationSessionState
): TitrationSessionState {
  return {
    ...current,
    dispensedMl: 0,
    tapOpenFraction: 0,
    airBubblePresent: false,
    indicatorAdded: false,
    swirled: false,
    analysisFeedback: null,
  };
}

export function recordAndRefillTitrationSession(
  current: TitrationSessionState
): TitrationSessionState {
  const recorded = recordCurrentTitrationTrial(current);
  return recorded === current ? current : refillForNextTitrationTrial(recorded);
}

export function createInitialExperimentSessions(): ExperimentSessionsState {
  return {
    titration: {
      dispensedMl: 0,
      tapOpenFraction: 0,
      airBubblePresent: true,
      eyeLevel: true,
      indicatorAdded: false,
      swirled: false,
      trials: [],
      answer: "",
      analysisFeedback: null,
    },
    electrical: {
      supplyVoltageV: 3,
      seriesResistanceOhm: 8,
      switchClosed: false,
      ammeterPlacement: "series",
      voltmeterPlacement: "across-lamp",
      ammeterRangeA: 2,
      voltmeterRangeV: 20,
      points: [],
      reason: "",
      analysisFeedback: null,
    },
  };
}
